import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setCompanyContext } from "../lib/api";
import { resumeSession, accessToken, saveSession, clearSession, getStoredCompany, storeCompany } from "../lib/session";

const AuthCtx = createContext(null);
const COMPANY_ROLES = ["company_admin", "company_manager", "company_finance", "company_hr"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const [activeCompany, setActiveCompanyState] = useState(() => {
    return getStoredCompany();
  });

  const applyCompany = (u, c) => {
    if (!u || COMPANY_ROLES.includes(u.role) || u.role === "employee") setCompanyContext(null);
    else setCompanyContext(c?.id || null);
  };

  const load = useCallback(async () => {
    if (!accessToken()) await resumeSession();
    if (!accessToken()) { setUser(false); return; }
    try {
      const [{ data }, m] = await Promise.all([api.get("/auth/me"), api.get("/meta")]);
      applyCompany(data, activeCompany);
      setMeta(m.data);
      setUser(data);
    } catch { setUser(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const login = async (email, password, remember = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    saveSession({ access: data.access_token, refresh: data.refresh_token, user: data.user, remember });
    setActiveCompanyState(null);
    applyCompany(data.user, null);
    const m = await api.get("/meta");
    setMeta(m.data);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    clearSession();
    setCompanyContext(null);
    setActiveCompanyState(null);
    setUser(false);
  };

  const setActiveCompany = (c) => {
    storeCompany(c);
    setCompanyContext(c?.id || null);
    setActiveCompanyState(c);
  };

  const can = (p) => !!user && user.perms?.includes(p);
  const isCompanyUser = !!user && COMPANY_ROLES.includes(user.role);
  const companyName = isCompanyUser ? user.company?.trade_name : activeCompany?.trade_name;

  return (
    <AuthCtx.Provider value={{ user, meta, login, logout, can, activeCompany, setActiveCompany, isCompanyUser, companyName, reload: load }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export const homeFor = (u) => ({ admin: "/backoffice", backoffice: "/backoffice", empresa: "/empresa", funcionario: "/funcionario" }[u?.env] || "/login");
